import React, { useEffect, useState, useCallback, useMemo } from "react";
import locationAPI from "../../../services/http/location.service";
import httpService from "../../../services/http/peoples/company.service";
import UnlimitInput from "../../../components/ui-kit/unlimit-input";
import UnlimitTextarea from "../../../components/ui-kit/unlimit-textarea";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { all_routes } from "../../../Router/all_routes";
import notificationService from "../../../services/toaster.service";
import { Link } from "react-router-dom/dist";
import UnlimitSelect from "../../../components/ui-kit/unlimit-select";
import UnlimitToggle from "../../../components/ui-kit/unlimit-toggle";
import { hydrateErrors } from "../../../utils/error.utils";
import UnlimitLoading from "../../../components/ui-kit/unlimit-loading";
import { store } from "../../../core/redux/store";
import UnlimitSubmitButton from "../../../components/ui-kit/unlimit-submit-button";
import UnlimitBreadcrumb from "../../../components/ui-kit/unlimit-breadcrumb";
import UnlimitFileUpload from "../../../components/ui-kit/unlimit-fileupload";
import { useTranslation } from "react-i18next";
import useRoleAndPerm from "../../../utils/permissionFunction";
import { useDispatch } from "react-redux";
import { fetchCompanies } from "../../../core/redux/reducers/companies.slice";

// Constants
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const ALLOWED_FILE_TYPES = ["image/jpeg", "image/png", "image/jpg"];

// Validation rules
const VALIDATION_RULES = {
  business_name: {
    required: true,
    maxLength: 50,
  },
  lm_company_number: {
    required: true,
    pattern: /^\d+$/,
  },
  admin_name: {
    required: true,
    pattern: /^[A-Za-z\s\-',.*&@]+$/,
    maxLength: 50,
  },
  admin_id: {
    required: true,
  },
  email: {
    required: true,
    pattern: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
  },
  phone: {
    required: true,
    pattern: /^(?:\+|00)?[\d\s\-()]+$/,
    maxLength: 20,
  },
  address: {
    required: true,
    maxLength: 50,
  },
  zip: {
    required: true,
    pattern: /^[1-9][0-9]*$/,
    maxLength: 10,
  },
  description: {
    required: true,
    maxLength: 500,
  },
};

const CompanyForm = () => {
  // Hooks
  const { hasPermission, getRedirectLink } = useRoleAndPerm();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { id } = useParams();

  // State management
  const [isLoading, setIsLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(false);
  const [imageUrl, setImageUrl] = useState("");
  const [existingImageUrl, setExistingImageUrl] = useState("");
  const [countries, setCountries] = useState([]);
  const [cities, setCities] = useState([]);
  const [citiesLoading, setCitiesLoading] = useState(false);

  // Form setup
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    getValues,
    control,
    setError,
    reset,
    clearErrors,
  } = useForm({
    defaultValues: {
      is_active: true,
    },
  });

  // Memoized values
  const isEditMode = useMemo(() => Boolean(id), [id]);
  const companyLink = useMemo(
    () => getRedirectLink("company"),
    [getRedirectLink]
  );
  const { locations } = useMemo(() => store.getState(), []);

  const breadcrumbItems = useMemo(
    () => [
      { label: "", link: "/", icon: "feather-home" },
      { label: t("company.company"), link: "/companies-list" },
      {
        label: isEditMode ? t("company.editCompany") : t("company.addCompany"),
        active: true,
      },
    ],
    [isEditMode, t]
  );

  // Validation rules with translations
  const getValidationRules = useCallback(
    (fieldName) => {
      const rules = VALIDATION_RULES[fieldName];
      if (!rules) return {};

      const validationRules = {};

      if (rules.required) {
        validationRules.required = t("inputField.validation.required");
      }

      if (rules.maxLength) {
        validationRules.maxLength = {
          value: rules.maxLength,
          message: t("inputField.validation.maxLength", {
            value: rules.maxLength,
          }),
        };
      }

      if (rules.pattern) {
        const patternMessages = {
          [VALIDATION_RULES.lm_company_number.pattern]: t(
            "inputField.validation.invalidNumber"
          ),
          [VALIDATION_RULES.admin_name.pattern]: t(
            "inputField.validation.adminNamePattern"
          ),
          [VALIDATION_RULES.email.pattern]: t(
            "inputField.validation.emailPattern"
          ),
          [VALIDATION_RULES.phone.pattern]: t(
            "inputField.validation.phonePattern"
          ),
          [VALIDATION_RULES.zip.pattern]: t(
            "inputField.validation.zipCodePattern"
          ),
        };

        validationRules.pattern = {
          value: rules.pattern,
          message:
            patternMessages[rules.pattern] ||
            t("inputField.validation.invalidFormat"),
        };
      }

      return validationRules;
    },
    [t]
  );

  // File validation
  const validateFile = useCallback(
    (file) => {
      if (!file) return null;

      if (!ALLOWED_FILE_TYPES.includes(file.type)) {
        return t("inputField.validation.invalidFileType");
      }

      if (file.size > MAX_FILE_SIZE) {
        return t("inputField.validation.fileTooLarge");
      }

      return null;
    },
    [t]
  );

  // Data hydration
  const hydratePredefinedValues = useCallback(
    async (data) => {
      const formValues = {
        business_name: data.business_name || "",
        lm_company_number: data.lm_company_number || "",
        admin_name: data.user?.name || "",
        admin_id: data.user?.admin_id || "",
        address: data.address || "",
        phone: data.phone || "",
        email: data.email || "",
        zip: data.zip || "",
        is_active: data.is_active == 1,
        description: data.description || "",
        city_id: data.city?.id || null,
        country_id: data.country?.id || null,
      };

      // Reset form with new values
      reset(formValues);
      setExistingImageUrl(data.logo || "");

      // Set selected country if exists
      if (data.country?.id) {
        const country = countries.find((item) => item.id === data.country.id);
        if (country) {
          const countryOption = {
            ...country,
            label: country.name,
            value: country.id,
          };
          setValue("country_id", countryOption);
        }
      }
    },
    [countries, reset, setValue]
  );

  // API calls
  const getCompany = useCallback(async () => {
    if (!id) return;

    try {
      setPageLoading(true);
      const response = await httpService.getCompany(id);

      if (response?.status === 200) {
        const data = response.data.data;
        await hydratePredefinedValues(data);

        if (data.country?.id) {
          await getCities(data.country.id);
        }
      }
    } catch (error) {
      console.error("Error fetching company:", error);
      notificationService.errorMessage(t("company.fetchError"));
    } finally {
      setPageLoading(false);
    }
  }, [id, hydratePredefinedValues, t]);

  const getCities = useCallback(
    async (countryId) => {
      if (!countryId) return;

      try {
        setCitiesLoading(true);
        setCities([]);
        setValue("city", null);
        clearErrors("city");

        const response = await locationAPI.getCitiesByCountry(countryId);

        if (response?.status === 200) {
          const citiesData = response.data.data?.data || [];
          setCities(citiesData);

          // Set selected city for edit mode
          if (isEditMode) {
            const cityId = getValues("city_id");
            const city = citiesData.find((item) => item.id === cityId);

            if (city) {
              const cityOption = {
                ...city,
                label: city.name,
                value: city.id,
              };
              setValue("city", cityOption);
            }
          }
        }
      } catch (error) {
        console.error("Error fetching cities:", error);
        notificationService.errorMessage(t("company.citiesFetchError"));
      } finally {
        setCitiesLoading(false);
      }
    },
    [setValue, clearErrors, isEditMode, getValues, t]
  );

  // Form submission
  const createFormData = useCallback(
    (data) => {
      const formData = new FormData();

      // Append all form fields
      Object.entries(data).forEach(([key, value]) => {
        if (key === "logo" && value?.[0]) {
          formData.append(key, value[0]);
        } else if (key === "is_active") {
          formData.append(key, value ? 1 : 0);
        } else if (key === "country_id" && value?.id) {
          formData.append(key, value.id);
        } else if (key === "city" && value?.id) {
          formData.append("city_id", value.id);
        } else if (value !== null && value !== undefined && key !== "city_id") {
          formData.append(key, value);
        }
      });

      if (isEditMode && id) {
        formData.append("id", id);
      }

      return formData;
    },
    [isEditMode, id]
  );

  const onSubmitForm = useCallback(
    async (data) => {
      // File validation
      if (data.logo?.[0]) {
        const fileError = validateFile(data.logo[0]);
        console.log("salman", fileError);
        
        if (fileError) {
          setError("logo", { type: "manual", message: fileError });
          return;
        }
      } else {
        clearErrors("logo");
        data.logo = null;
      }

      try {
        setIsLoading(true);
        const formData = createFormData(data);

        if (isEditMode) {
          await updateCompany(formData);
        } else {
          await createCompany(formData);
        }
      } catch (error) {
        console.error("Form submission error:", error);
      } finally {
        setIsLoading(false);
      }
    },
    [isEditMode, validateFile, createFormData, setError]
  );

  const updateCompany = useCallback(
    async (formData) => {
      try {
        const response = await httpService.updateCompany(formData);

        if (response.status === 200) {
          notificationService.successMessage(t("company.companyUpdateSuccess"));

          if (hasPermission(["company-view"])) {
            dispatch(fetchCompanies(true));
          }

          setTimeout(() => navigate(all_routes.companiesList), 1000);
        }
      } catch (error) {
        if (error.validation_error?.errors) {
          hydrateErrors(error.validation_error.errors, setError);
        } else {
          notificationService.errorMessage(t("company.updateError"));
        }
      }
    },
    [t, hasPermission, dispatch, navigate, setError]
  );

  const createCompany = useCallback(
    async (formData) => {
      try {
        const response = await httpService.createCompany(formData);

        if (response?.status === 200) {
          notificationService.successMessage(t("company.companyCreateSuccess"));

          if (hasPermission(["company-view"])) {
            dispatch(fetchCompanies(true));
          }

          navigate(all_routes.companiesList);
        }
      } catch (error) {
        if (error.validation_error?.errors) {
          hydrateErrors(error.validation_error.errors, setError);
        } else {
          notificationService.errorMessage(t("company.createError"));
        }
      }
    },
    [t, hasPermission, dispatch, navigate, setError]
  );

  // Event handlers
  const onChangeCountry = useCallback(
    (value) => {
      if (value) {
        getCities(value.id);
      } else {
        setCities([]);
        setValue("city", null);
      }
    },
    [getCities, setValue]
  );

  const handleImageChange = useCallback(
    (event) => {
      const files = event.target.files;
      if (files?.length > 0) {
        const file = files[0];
        const fileError = validateFile(file);

        if (fileError) {
          setError("logo", { type: "manual", message: fileError });
          return;
        }

        clearErrors("logo");
        setImageUrl(URL.createObjectURL(file));
      }
    },
    [validateFile, setError, clearErrors]
  );

  // Effects
  useEffect(() => {
    setCountries(locations.countries || []);
  }, [locations.countries]);

  useEffect(() => {
    if (isEditMode) {
      getCompany();
    }
  }, [isEditMode, getCompany]);

  // Cleanup effect for object URLs
  useEffect(() => {
    return () => {
      if (imageUrl) {
        URL.revokeObjectURL(imageUrl);
      }
    };
  }, [imageUrl]);

  // Render helpers
  const renderImagePreview = () => {
    const src = isEditMode ? imageUrl || existingImageUrl : imageUrl;

    if (!src) return null;

    return (
      <img
        src={src}
        alt="Company Logo"
        className="img-fluid ms-2"
        style={{ maxWidth: "70px", maxHeight: "70px" }}
        onError={(e) => {
          console.error("Image load error:", e);
          e.target.style.display = "none";
        }}
      />
    );
  };

  const renderForm = () => {
    if (pageLoading) {
      return (
        <div className="row d-flex justify-content-center">
          <UnlimitLoading width={50} height={50} />
        </div>
      );
    }

    return (
      <div>
        {/* Row 1: Company Info */}
        <div className="row">
          <div className="col-md-4">
            <UnlimitInput
              label={t("inputField.label.companyName")}
              id="business_name"
              type="text"
              placeholder="Enter Company Name"
              register={register}
              rules={getValidationRules("business_name")}
              error={errors.business_name}
              errorMessage={errors.business_name?.message}
            />
          </div>
          <div className="col-md-4">
            <UnlimitInput
              label={t("inputField.label.lmCompanyNo")}
              id="lm_company_number"
              type="text"
              placeholder="Enter LM Company No"
              register={register}
              rules={getValidationRules("lm_company_number")}
              error={errors.lm_company_number}
              errorMessage={errors.lm_company_number?.message}
            />
          </div>
          <div className="col-md-4">
            <UnlimitFileUpload
              label={t("inputField.label.logo")}
              id="logo"
              register={register}
              accept=".jpeg, .png, .jpg"
              error={errors.logo}
              rules={{
                required: !isEditMode && t("inputField.validation.required"),
              }}
              errorMessage={errors.logo?.message}
              onChange={handleImageChange}
            />
            {renderImagePreview()}
          </div>
        </div>

        {/* Row 2: Admin & Contact Info */}
        <div className="row">
          <div className="col-md-3">
            <UnlimitInput
              label={t("inputField.label.adminName")}
              id="admin_name"
              type="text"
              placeholder="Enter Administrator Name"
              register={register}
              rules={getValidationRules("admin_name")}
              error={errors.admin_name}
              errorMessage={errors.admin_name?.message}
            />
          </div>
          <div className="col-md-3">
            <UnlimitInput
              label={t("inputField.label.adminID")}
              id="admin_id"
              type="text"
              placeholder={t("inputField.placeholder.enterAdminID")}
              register={register}
              rules={getValidationRules("admin_id")}
              error={errors.admin_id}
              errorMessage={errors.admin_id?.message}
            />
          </div>
          <div className="col-md-3">
            <UnlimitInput
              label={t("inputField.label.email")}
              id="email"
              type="email"
              placeholder="Enter Email"
              register={register}
              rules={getValidationRules("email")}
              error={errors.email}
              errorMessage={errors.email?.message}
            />
          </div>
          <div className="col-md-3">
            <UnlimitInput
              label={t("inputField.label.phone")}
              id="phone"
              type="text"
              placeholder="Enter Phone"
              register={register}
              rules={getValidationRules("phone")}
              error={errors.phone}
              errorMessage={errors.phone?.message}
            />
          </div>
        </div>

        {/* Row 3: Location Info */}
        <div className="row">
          <div className="col-md-3">
            <UnlimitInput
              label={t("inputField.label.address")}
              id="address"
              type="text"
              placeholder="Enter Address"
              register={register}
              rules={getValidationRules("address")}
              error={errors.address}
              errorMessage={errors.address?.message}
            />
          </div>
          <div className="col-md-3">
            <UnlimitInput
              label={t("inputField.label.zipCode")}
              id="zip"
              type="text"
              placeholder="Enter Zip Code"
              register={register}
              rules={getValidationRules("zip")}
              error={errors.zip}
              errorMessage={errors.zip?.message}
            />
          </div>
          <div className="col-md-3">
            <UnlimitSelect
              label={t("inputField.label.country")}
              id="country_id"
              errors={errors}
              control={control}
              register={register}
              options={countries?.filter((country) => country.flag) || []}
              rules={getValidationRules("country_id")}
              placeholder="Select"
              optionLabel="name"
              optionValue="id"
              onChange={onChangeCountry}
            />
          </div>
          <div className="col-md-3">
            <UnlimitSelect
              label={t("inputField.label.city")}
              id="city"
              errors={errors}
              control={control}
              register={register}
              options={cities?.filter((city) => city.name) || []}
              rules={getValidationRules("city")}
              placeholder="Select"
              optionLabel="name"
              optionValue="id"
              isLoading={citiesLoading}
            />
          </div>
        </div>

        {/* Row 4: Description */}
        <div className="row">
          <div className="col-lg-12">
            <div className="mb-3">
              <UnlimitTextarea
                label={t("inputField.label.description")}
                id="description"
                placeholder="Enter Description"
                register={register}
                rules={getValidationRules("description")}
                error={errors.description}
                errorMessage={errors.description?.message}
              />
            </div>
          </div>
        </div>

        {/* Row 5: Status Toggle */}
        <div className="row">
          <div className="col-md-12">
            <div className="input-blocks mb-3 d-flex justify-content-start">
              <UnlimitToggle
                isChecked={getValues("is_active")}
                label={t("inputField.label.status")}
                id="is_active"
                register={register}
              />
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="col-lg-12 mb-4">
          <Link to={companyLink} className="me-2">
            <button type="button" className="btn btn-cancel">
              {t("general.button.cancel")}
            </button>
          </Link>
          <UnlimitSubmitButton
            label={
              isEditMode
                ? t("general.button.update")
                : t("general.button.submit")
            }
            isLoading={isLoading}
          />
        </div>
      </div>
    );
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex flex-column">
            <div className="page-title">
              <h4>
                {isEditMode
                  ? t("company.editCompany")
                  : t("company.addNewCompany")}
              </h4>
            </div>
            <UnlimitBreadcrumb items={breadcrumbItems} />
          </div>
        </div>

        <form onSubmit={handleSubmit(onSubmitForm)}>
          <div className="card">
            <div className="card-body pb-0">{renderForm()}</div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CompanyForm;
